//libs
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

//styles
import {
  Wrapper,
  FilterAndContentContainer,
  ListingContainerHeader,
  Loader,
  LoaderWrapper,
  ListingContainer,
  FilterContainer,
  ScrollToTop,
  BreadCrumb,
} from './styles';

//selectors
import {
  getBannerImages,
  getIsFetchingWithFilters,
  getTitle,
  getBreadCrumbs,
  getStoreRedirect,
  getTotalCount,
  getStoreRedirectCount,
  getIsSearch,
} from 'shared/store/listingV2/selectors';
import { getDynamicPageKeyWord } from 'shared/store/dynamicPage/selector';

//components
import Title from 'shared/layouts/Listing/Desktop/components/Layout/components/Title';
import Products from 'shared/layouts/Listing/Desktop/components/Layout/components/Products';
import Filter from 'shared/layouts/Listing/Desktop/components/Layout/components/Filter';
import TopBanner from 'shared/layouts/Listing/Desktop/components/Layout/components/TopBanner';
import AppliedFiltersAndSort from './components/AppliedFiltersAndSort';
import LoginNudgeProvider from 'shared/components/Nudge';

//hooks
import { useAppLayoutContext } from 'shared/layouts/AppLayout/AppLayoutContext';
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';

//helpers
import { getContainerTop } from './helpers';
import throttle from 'lodash/throttle';
import Storage from 'shared/helpers/storage';

//constants
import { FILTERS_LS_NAME, STICKY_VALUES, TITLE_HEIGHT } from './constants';
import ListingFooterV2 from 'shared/components/Footer/components/ListingFooterV2';
import { DP_HOME } from 'shared/layouts/Listing/constants';
import StoreBanner from 'shared/components/StoreBanner';
import { PAGENAME } from 'shared/components/Nudge/constants';
import LoginNudgeTracker from 'shared/components/Nudge/LoginNudgeTracker';

const throttledCollapseFilter = throttle(
  (setIsFiltersExpanded, state) => {
    setIsFiltersExpanded(state);
  },
  500,
  { trailing: false }
);

const Layout = () => {
  const bannerImages = useSelector(getBannerImages);
  const isFetchingWithFilters = useSelector(getIsFetchingWithFilters);
  const storeRedirect = useSelector(getStoreRedirect);
  const totalProducts = useSelector(getTotalCount);
  const redirectStoreProductCount = useSelector(getStoreRedirectCount);
  const isSearch = useSelector(getIsSearch);
  const isDpBreadCrumbsEnabled = useRemoteConfig('isDpBreadCrumbsEnabled');
  const dynamicPageKeyWord = useSelector(getDynamicPageKeyWord);
  const breadCrumbsFromState = useSelector(getBreadCrumbs);
  const breadCrumbs =
    dynamicPageKeyWord && isDpBreadCrumbsEnabled
      ? [DP_HOME, { key: dynamicPageKeyWord }]
      : breadCrumbsFromState;
  const productTitle = useSelector(getTitle);
  const title = dynamicPageKeyWord && isDpBreadCrumbsEnabled ? dynamicPageKeyWord : productTitle;

  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const [whoIsSticky, setIsSticky] = useState(STICKY_VALUES.NONE);
  const headerContainerRef = useRef<HTMLDivElement>();
  const filterRef = useRef<HTMLDivElement>();
  const leftMargin = useRef(0);
  const showPromotionalBanner = useRemoteConfig('showPromotionalBanner');
  const { isSubMenuVisible } = useAppLayoutContext();

  const _setIsExpanded = (state: boolean) => {
    throttledCollapseFilter(setIsFiltersExpanded, state);
  };

  const updateExpanded = useCallback(() => {
    throttledCollapseFilter(setIsFiltersExpanded, false);

    const containerTop = headerContainerRef.current?.getBoundingClientRect().top;
    const filterRight = filterRef.current?.getBoundingClientRect().right;
    const stickyTopValue = getContainerTop(isSubMenuVisible, showPromotionalBanner);
    const titleTopValue = containerTop - TITLE_HEIGHT;
    const isStickyBoth = whoIsSticky === STICKY_VALUES.BOTH;

    //scrolling down
    if (!isStickyBoth) {
      if (containerTop <= stickyTopValue) {
        // when both are  sticky
        leftMargin.current = filterRight;
        setIsSticky(STICKY_VALUES.BOTH);
        return;
      }
      if (whoIsSticky !== STICKY_VALUES.TITLE && titleTopValue <= stickyTopValue) {
        // when title is sticky and applied filters are not
        setIsSticky(STICKY_VALUES.TITLE);
        return;
      }
    }

    // scrolling up
    if (containerTop > stickyTopValue && containerTop < stickyTopValue + TITLE_HEIGHT) {
      // when title is sticky and applied filters are not
      setIsSticky(STICKY_VALUES.TITLE);
    } else if (containerTop > stickyTopValue) {
      // when both are not sticky
      setIsSticky(STICKY_VALUES.NONE);
    }
  }, [isSubMenuVisible, whoIsSticky, showPromotionalBanner]);

  useEffect(() => {
    window.addEventListener('scroll', updateExpanded, { passive: true });
    window.addEventListener('beforeunload', () => {
      Storage.removeItem(FILTERS_LS_NAME);
    });
    return () => {
      window.removeEventListener('scroll', updateExpanded);
    };
  }, [updateExpanded]);

  const isContainerSticky = whoIsSticky === STICKY_VALUES.BOTH;
  const isTitleSticky = whoIsSticky === STICKY_VALUES.TITLE || whoIsSticky === STICKY_VALUES.BOTH;

  useEffect(() => {
    if (!isFetchingWithFilters) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isFetchingWithFilters]);

  return (
    <>
      <Wrapper>
        {isFetchingWithFilters && (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        )}

        <Title
          isSticky={isTitleSticky}
          containerTop={getContainerTop(isSubMenuVisible, showPromotionalBanner)}
        />
        {bannerImages?.length > 0 && <TopBanner />}
        <Title // for all products
          isSticky={isTitleSticky}
          containerTop={getContainerTop(isSubMenuVisible, showPromotionalBanner)}
          isAllProducts={true}
        />

        <BreadCrumb breadCrumbs={breadCrumbs} productTitle={title} />

        <ListingContainer>
          <LoginNudgeProvider page={PAGENAME.PLP}>
            <LoginNudgeTracker pageType={PAGENAME.PLP} />
            <FilterContainer ref={filterRef}>
              <Filter stickyTopValue={getContainerTop(isSubMenuVisible, showPromotionalBanner)} />
            </FilterContainer>

            <FilterAndContentContainer>
              <ListingContainerHeader
                ref={headerContainerRef}
                containerTop={getContainerTop(isSubMenuVisible, showPromotionalBanner)}
                isSticky={isContainerSticky}
                leftMargin={leftMargin.current}
              >
                <AppliedFiltersAndSort
                  isFiltersExpanded={isFiltersExpanded}
                  setIsFiltersExpanded={_setIsExpanded}
                  isSticky={isContainerSticky}
                />
              </ListingContainerHeader>
              <Products />
              <StoreBanner
                isSearch={isSearch}
                currentStoreProductCount={totalProducts}
                redirectStoreProductCount={redirectStoreProductCount}
                storeToRedirect={storeRedirect}
              />
            </FilterAndContentContainer>
          </LoginNudgeProvider>
        </ListingContainer>
        <ScrollToTop />
      </Wrapper>
      <Wrapper>
        <ListingFooterV2 />
      </Wrapper>
    </>
  );
};
export default memo(Layout);
